import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  listWantItems,
  createWantItem,
  deleteWantItem,
  updateWantItem,
  refreshWantMatches,
  listWantMatches,
} from "@/lib/want-list.functions";
import { formatGBP } from "@/lib/format";
import { buildAuctionSlug } from "@/lib/auction-slug";
import { useTranslation } from "@/i18n";

export const Route = createFileRoute("/_authenticated/want-list")({
  head: () => ({
    meta: [
      { title: "Want List — BidNova Auctions" },
      { name: "description", content: "Track cards you're hunting for and get matched to active BidNova auctions." },
    ],
  }),
  component: WantListPage,
  errorComponent: ({ error }) => <div className="p-6 text-rose-400">{(error as Error).message}</div>,
  notFoundComponent: () => <div className="p-6">Not found.</div>,
});

function WantListPage() {
  const { t } = useTranslation("activity");
  const qc = useQueryClient();
  const listFn = useServerFn(listWantItems);
  const createFn = useServerFn(createWantItem);
  const deleteFn = useServerFn(deleteWantItem);
  const updateFn = useServerFn(updateWantItem);
  const refreshFn = useServerFn(refreshWantMatches);
  const matchesFn = useServerFn(listWantMatches);

  const items = useQuery({ queryKey: ["want-list"], queryFn: () => listFn() });
  const matches = useQuery({ queryKey: ["want-matches"], queryFn: () => matchesFn() });

  const [cardName, setCardName] = useState("");
  const [cardSet, setCardSet] = useState("");
  const [priceMax, setPriceMax] = useState("");

  const createMut = useMutation({
    mutationFn: async () =>
      createFn({
        data: {
          card_name: cardName,
          card_set: cardSet || null,
          price_max_pence: priceMax ? Math.round(parseFloat(priceMax) * 100) : null,
        },
      }),
    onSuccess: () => {
      setCardName(""); setCardSet(""); setPriceMax("");
      toast.success(t("wantList.toastAdded", "Added to want list"));
      qc.invalidateQueries({ queryKey: ["want-list"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const refreshMut = useMutation({
    mutationFn: async () => refreshFn(),
    onSuccess: (r: { matches: number }) => {
      toast.success(t("wantList.toastMatchesFound", "Found {count} matches", { count: r.matches }));
      qc.invalidateQueries({ queryKey: ["want-matches"] });
    },
  });

  return (
    <div className="container max-w-4xl py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t("wantList.title", "Want List")}</h1>
          <p className="text-sm text-muted-foreground">{t("wantList.subtitle", "Track cards you're hunting — we'll flag matching active auctions.")}</p>
        </div>
        <Button variant="outline" onClick={() => refreshMut.mutate()} disabled={refreshMut.isPending}>
          {refreshMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          {t("wantList.refreshMatches", "Refresh matches")}
        </Button>
      </div>

      <div className="rounded-xl border border-border/60 bg-surface-2/40 p-4 space-y-3">
        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <Label>{t("wantList.cardName", "Card name *")}</Label>
            <Input value={cardName} onChange={(e) => setCardName(e.target.value)} placeholder={t("wantList.cardNamePlaceholder", "Charizard")} />
          </div>
          <div>
            <Label>{t("wantList.set", "Set")}</Label>
            <Input value={cardSet} onChange={(e) => setCardSet(e.target.value)} placeholder={t("wantList.setPlaceholder", "Base Set")} />
          </div>
          <div>
            <Label>{t("wantList.maxPrice", "Max price (£)")}</Label>
            <Input value={priceMax} onChange={(e) => setPriceMax(e.target.value)} inputMode="decimal" placeholder="500" />
          </div>
        </div>
        <Button onClick={() => cardName && createMut.mutate()} disabled={!cardName || createMut.isPending}>
          <Plus className="h-4 w-4 mr-2" /> {t("wantList.addButton", "Add to want list")}
        </Button>
      </div>

      <div>
        <h2 className="text-lg font-medium mb-2">{t("wantList.yourWantList", "Your want list")}</h2>
        <div className="space-y-2">
          {(items.data ?? []).map((it) => (
            <div key={it.id} className="rounded-lg border border-border/40 bg-background/40 p-3 flex items-center justify-between">
              <div className="text-sm">
                <div className="font-medium">{it.card_name}</div>
                <div className="text-xs text-muted-foreground">
                  {it.card_set ?? t("wantList.anySet", "any set")}{it.price_max_pence ? ` · ≤ ${formatGBP(it.price_max_pence)}` : ""} · {it.active ? t("wantList.active", "active") : t("wantList.paused", "paused")}
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => updateFn({ data: { id: it.id, active: !it.active } }).then(() => qc.invalidateQueries({ queryKey: ["want-list"] }))}>
                  {it.active ? t("wantList.pause", "Pause") : t("wantList.resume", "Resume")}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => deleteFn({ data: { id: it.id } }).then(() => qc.invalidateQueries({ queryKey: ["want-list"] }))}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
          {(items.data ?? []).length === 0 && <p className="text-sm text-muted-foreground">{t("wantList.noItemsYet", "No items yet.")}</p>}
        </div>
      </div>

      <div>
        <h2 className="text-lg font-medium mb-2">{t("wantList.matches", "Matches")}</h2>
        <div className="space-y-2">
          {(matches.data ?? []).map((m) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const auc = (m as any).auctions;
            const href = auc?.id
              ? `/auction/${buildAuctionSlug({ id: auc.id, title: auc.title, grading_company: auc.grading_company, grade: auc.grade })}`
              : "/auctions";
            return (
              <a key={m.id} href={href} className="block rounded-lg border border-border/40 bg-background/40 p-3 hover:border-gold">
                <div className="text-sm font-medium">{auc?.title}</div>
                <div className="text-xs text-muted-foreground">{t("wantList.matched", "Matched {date}", { date: new Date(m.first_seen_at).toLocaleString() })}</div>
              </a>
            );
          })}
          {(matches.data ?? []).length === 0 && <p className="text-sm text-muted-foreground">{t("wantList.noMatchesYet", "No matches yet — click Refresh matches.")}</p>}
        </div>
      </div>
    </div>
  );
}
